/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../JSG');
const Node = require('./Node');
const ItemAttributes = require('../attr/ItemAttributes');
const StreamSheetsContainer = require('./StreamSheetsContainer');
const OutboxContainer = require('./OutboxContainer');
const SplitterNode = require('./SplitterNode');
const FormatAttributes = require('../attr/FormatAttributes');

const MACHINE_STATE = {
	RUNNING: 'running',
	STOPPED: 'stopped',
	PAUSED: 'paused',
};

/**
 * @class MachineContainer

 * @extends Node
 * @constructor
 */
module.exports = class MachineContainer extends Node {
	constructor() {
		super();

		this._sheetsContainer = new StreamSheetsContainer();
		this.addItem(this._sheetsContainer);

		// splitter
		this._splitter = new SplitterNode();
		this._splitter.setDirection(ItemAttributes.Direction.VERTICAL);
		this.addItem(this._splitter);

		this._outboxContainer = new OutboxContainer();
		this._outboxContainer.setWidth(5000);
		this.addItem(this._outboxContainer);

		this.getFormat().setLineStyle(FormatAttributes.LineStyle.NONE);
		this.getFormat().setFillColor(JSG.theme.graph);
		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setContainer(false);

		this._splitter.setItemToResize(this._outboxContainer);

		this._machineId = '';
		this._description = '';
		this._outboxVisible = false;
		this._splitView = false;
		this._machineState = MACHINE_STATE.STOPPED;
	}

	init() {
		this._sheetsContainer.init();
		this.splitView = false;
	}


	static get MACHINE_STATE() {
		return MACHINE_STATE;
	}

	newInstance() {
		return new MachineContainer();
	}

	_assignName(id) {
		this.setName(`MachineContainer${id}`);
	}

	get machineId() {
		return this._machineId;
	}

	set machineId(id) {
		this._machineId = id;
	}

	get description() {
		return this._description;
	}

	set description(description) {
		this._description = description;
	}

	get maximizeSheet() {
		return this._maximizeSheet;
	}

	set maximizeSheet(state) {
		this._maximizeSheet = state;
	}

	get splitView() {
		return this._splitView;
	}

	set splitView(state) {
		this._splitView = state;
		this._sheetsContainer.rightSheetContainer.getItemAttributes().setVisible(state);
		// this._sheetsContainer.splitter.getItemAttributes().setVisible(state);
	}

	get machineState() {
		return this._machineState;
	}

	set machineState(state) {
		this._machineState = state;
	}

	get isRunning() {
		return this._machineState === MACHINE_STATE.RUNNING;
	}

	get outboxVisible() {
		return this._outboxVisible
	}

	set outboxVisible(visible) {
		this._outboxVisible = visible;
	}

	isAddLabelAllowed() {
		return false;
	}

	get streamSheetsContainer() {
		return this._sheetsContainer;
	}

	getStreamSheetsContainer() {
		return this._sheetsContainer;
	}

	getSplitter() {
		return this._splitter;
	}

	get outboxContainer() {
		return this._outboxContainer;
	}

	getItemType() {
		return 'machinecontainer';
	}

	isOutboxVisible() {
		const graph = this.graph;
		return graph.showViewMode ? graph.viewSettings.showOutbox: this.outboxVisible;
	}

	layout() {
		const box = JSG.boxCache.get();
		const size = this.getSize().toPoint();
		const sizeOutbox = this._outboxContainer.getSize().toPoint();
		const outbox = this.isOutboxVisible();
		const margin = 200;
		const viewSettings = this.graph.viewSettings;
		const frame = this.graph.showViewMode && (viewSettings.showOutbox || viewSettings.showInbox) ? 200 : 0;

		this._outboxContainer.getItemAttributes().setVisible(outbox);
		this._outboxContainer.updateSubAttributes();
		// this._splitter.getItemAttributes().setVisible(outbox);

		box.setLeft(frame);
		box.setTop(frame);
		box.setWidth(size.x - (outbox ? sizeOutbox.x + margin : 20) - frame * 2);
		box.setHeight(size.y - frame * 2);

		this._sheetsContainer.setBoundingBoxTo(box);
		this._sheetsContainer.layout();

		if (outbox) {
			box.setLeft(size.x - sizeOutbox.x - 20 - frame);
			box.setWidth(sizeOutbox.x);

			this._outboxContainer.setBoundingBoxTo(box);
			this._outboxContainer.layout();

			box.setLeft(size.x - sizeOutbox.x - margin - 20);
			box.setWidth(margin);

			this._splitter.setBoundingBoxTo(box);
		} else {
			this._outboxContainer.getBoundingBox(box);
			box.setLeft(box.getWidth() / 2);
			box.setTop(0);
			box.setHeight(0);
			this._outboxContainer.setBoundingBoxTo(box);

			box.setHeight(size.y);
			box.setLeft(size.x - margin);
			box.setWidth(0);
			box.setHeight(0);

			this._splitter.setBoundingBoxTo(box);
		}

		JSG.boxCache.release(box);

		super.layout();
	}
};
